import { create } from "zustand"
import { persist } from "zustand/middleware"

import type { FeedbackWithRelations } from "@productlane/zero/queries"

import type { AssignedSubTab } from "@/screens/app/inbox/helpers/assigned-sub-tab"
import type { ThreadsCursor } from "@/screens/app/inbox/shared-queries"
import { migratePersistedInboxState } from "@/screens/app/inbox/helpers/migrate-inbox-state"

export const DEFAULT_THREAD_CURSOR: ThreadsCursor = Object.freeze({
	cursor: undefined,
	direction: "forward",
	page: 1,
})

export type ThreadAnchorCursor = Pick<
	FeedbackWithRelations,
	| "id"
	| "lastStateChangeAt"
	| "lastMessageAt"
	| "createdAt"
	| "painLevel"
	| "snoozedUntil"
>

export interface ThreadAnchor {
	cursor?: ThreadAnchorCursor
	direction: "forward" | "backward"
	index: number
}

export const TOP_THREAD_ANCHOR: ThreadAnchor = Object.freeze({
	cursor: undefined,
	direction: "forward",
	index: 0,
})

export type InboxSortTypes = "important" | "newest" | "oldest"

export type SortByColumn = "lastMessageAt" | "painLevel" | "snoozedUntil"
export type SortByColumnOrder = "asc" | "desc"

const sortByColumnMap: Record<InboxSortTypes, SortByColumn> = {
	important: "painLevel",
	newest: "lastMessageAt",
	oldest: "lastMessageAt",
}

const sortByColumnMapDone: Record<InboxSortTypes, SortByColumn> = {
	important: "painLevel",
	newest: "lastMessageAt",
	oldest: "lastMessageAt",
}

const sortByQueryColumnOrderMap: Record<InboxSortTypes, SortByColumnOrder> = {
	important: "asc",
	newest: "desc",
	oldest: "asc",
}

interface SortState {
	groupByImportance: boolean
	setGroupByImportance: (value: boolean) => void
	sortBy: InboxSortTypes
	sortByQueryColumn: SortByColumn
	sortByQueryDoneColumn: SortByColumn
	sortByQueryColumnOrder: SortByColumnOrder
	setSortBy: (value: InboxSortTypes) => void
}

export const useSortState = create<SortState>()(
	persist(
		(set) => ({
			groupByImportance: true,
			setGroupByImportance: (value) => {
				set({ groupByImportance: value })
			},
			sortBy: "newest",
			sortByQueryColumn: sortByColumnMap.newest,
			sortByQueryDoneColumn: sortByColumnMapDone.newest,
			sortByQueryColumnOrder: sortByQueryColumnOrderMap.newest,
			setSortBy: (value) => {
				set({
					sortBy: value,
					sortByQueryColumn: sortByColumnMap[value],
					sortByQueryDoneColumn: sortByColumnMapDone[value],
					sortByQueryColumnOrder: sortByQueryColumnOrderMap[value],
				})
			},
		}),
		{ name: "sort-state" },
	),
)

// Neighbours of the open thread as the rendered inbox list orders them. An
// unset side means the list can't answer, so navigation falls back to its
// cursor query.
export interface InboxNavNeighbors {
	threadId: string
	prevId?: string
	nextId?: string
}

export type InboxState =
	| "needs-response"
	| "new"
	| "open"
	| "my"
	| "my-snoozed"
	| "my-done"
	| "my-close-loop"
	| "snoozed"
	| "done"
	| "close-loop"
	| "sla-breached"
	| "custom-view"

interface ThreadState {
	inboxState: InboxState
	setInboxState: (value: InboxState) => void
	activeCustomViewId: string | null
	setActiveCustomViewId: (id: string | null) => void
	pendingEditCustomViewId: string | null
	setPendingEditCustomViewId: (id: string | null) => void
	isEditingCustomView: boolean
	setIsEditingCustomView: (value: boolean) => void
	offset: number
	setOffset: (value: number) => void
	total: number | undefined
	setTotal: (value: number | undefined) => void
	estimatedTotal: number
	setEstimatedTotal: (value: number) => void
	threadAnchor: ThreadAnchor
	setThreadAnchor: (value: ThreadAnchor) => void
	assignedSubTab: AssignedSubTab
	setAssignedSubTab: (value: AssignedSubTab) => void
	inboxSearchThreadIds: string[]
	setInboxSearchThreadIds: (ids: string[]) => void
	inboxNavNeighbors: InboxNavNeighbors | null
	setInboxNavNeighbors: (neighbors: InboxNavNeighbors | null) => void
}

export const useThreadState = create<ThreadState>()(
	persist(
		(set, get) => ({
			inboxState: "new",
			setInboxState: (value) => {
				set({ inboxState: value })
			},
			activeCustomViewId: null,
			setActiveCustomViewId: (id) => {
				set({ activeCustomViewId: id })
			},
			pendingEditCustomViewId: null,
			setPendingEditCustomViewId: (id) => {
				set({ pendingEditCustomViewId: id })
			},
			isEditingCustomView: false,
			setIsEditingCustomView: (value) => {
				set({ isEditingCustomView: value })
			},
			offset: 0,
			setOffset: (value) => {
				set({ offset: value })
			},
			total: 0,
			setTotal: (total) => {
				set({ total })
			},
			estimatedTotal: 0,
			setEstimatedTotal: (estimatedTotal) => {
				set({ estimatedTotal })
			},
			threadAnchor: TOP_THREAD_ANCHOR,
			setThreadAnchor: (threadAnchor: ThreadAnchor) => {
				set({ threadAnchor })
			},
			assignedSubTab: "open",
			// Guarded: zustand notifies on every set, even with an unchanged value.
			setAssignedSubTab: (assignedSubTab) => {
				if (get().assignedSubTab !== assignedSubTab) set({ assignedSubTab })
			},
			inboxSearchThreadIds: [],
			setInboxSearchThreadIds: (inboxSearchThreadIds) => {
				const prev = get().inboxSearchThreadIds
				if (
					prev.length === inboxSearchThreadIds.length &&
					prev.every((id, i) => id === inboxSearchThreadIds[i])
				) {
					return
				}
				set({ inboxSearchThreadIds })
			},
			inboxNavNeighbors: null,
			setInboxNavNeighbors: (inboxNavNeighbors) => {
				const prev = get().inboxNavNeighbors
				if (
					prev?.threadId === inboxNavNeighbors?.threadId &&
					prev?.prevId === inboxNavNeighbors?.prevId &&
					prev?.nextId === inboxNavNeighbors?.nextId
				) {
					return
				}
				set({ inboxNavNeighbors })
			},
		}),
		{
			name: "thread-state",
			partialize: (state) => ({
				inboxState: state.inboxState,
				assignedSubTab: state.assignedSubTab,
				activeCustomViewId: state.activeCustomViewId,
				offset: state.offset,
			}),
			merge: (persisted, current) => {
				const stored =
					persisted && typeof persisted === "object"
						? (persisted as Partial<ThreadState>)
						: {}
				return {
					...current,
					...stored,
					inboxState: migratePersistedInboxState(
						stored.inboxState ?? current.inboxState,
					),
				}
			},
		},
	),
)

export type RoadmapTab = "active" | "hidden" | "all" | "pending"
interface RoadmapState {
	lastPage: RoadmapTab
	setInboxState: (value: RoadmapTab) => void
}

export const useRoadmapState = create<RoadmapState>()(
	persist(
		(set) => ({
			lastPage: "active",
			setInboxState: (value) => {
				set({ lastPage: value })
			},
		}),
		{ name: "roadmap-state" },
	),
)

export type ReportingTab =
	| "overview"
	| "conversations"
	| "team"
	| "responsiveness"
	| "slas"
	| "feedback"

type DateRangeType =
	| "7days"
	| "4weeks"
	| "3months"
	| "12months"
	| "all"
	| "custom"

export interface PersistedReportingFilters {
	origins: string[]
	assigneeIds: Array<string | null>
	tagIds: string[]
	companyIds: Array<string | null>
}

interface ReportingState {
	activeTab: ReportingTab
	setActiveTab: (value: ReportingTab) => void
	rangeType: DateRangeType
	setRangeType: (value: DateRangeType) => void
	customStartDate: string | null
	customEndDate: string | null
	setCustomDateRange: (start: Date, end: Date) => void
	filters: PersistedReportingFilters
	setFilters: (value: PersistedReportingFilters) => void
}

export const EMPTY_PERSISTED_REPORTING_FILTERS: PersistedReportingFilters = {
	origins: [],
	assigneeIds: [],
	tagIds: [],
	companyIds: [],
}

export const useReportingState = create<ReportingState>()(
	persist(
		(set) => ({
			activeTab: "overview",
			setActiveTab: (value) => set({ activeTab: value }),
			rangeType: "4weeks",
			setRangeType: (value) => set({ rangeType: value }),
			customStartDate: null,
			customEndDate: null,
			setCustomDateRange: (start, end) =>
				set({
					customStartDate: start.toISOString(),
					customEndDate: end.toISOString(),
				}),
			filters: EMPTY_PERSISTED_REPORTING_FILTERS,
			setFilters: (value) => set({ filters: value }),
		}),
		{ name: "reporting-state" },
	),
)

interface TrySectionState {
	dismissed: boolean
	dismiss: () => void
}

export const useTrySectionState = create<TrySectionState>()(
	persist(
		(set) => ({
			dismissed: false,
			dismiss: () => {
				set({ dismissed: true })
			},
		}),
		{ name: "try-section-state" },
	),
)

interface AiResolutionCapBannerState {
	dismissedKey: string | null
	dismiss: (key: string) => void
	reopen: () => void
}

export const useAiResolutionCapBannerState =
	create<AiResolutionCapBannerState>()(
		persist(
			(set) => ({
				dismissedKey: null,
				dismiss: (key) => set({ dismissedKey: key }),
				reopen: () => set({ dismissedKey: null }),
			}),
			{ name: "ai-resolution-cap-banner-state" },
		),
	)

export type InboxViewMode = "focus" | "list"

interface InboxViewState {
	inboxViewMode: InboxViewMode
	setInboxViewMode: (value: InboxViewMode) => void
}

export const useInboxViewState = create<InboxViewState>()(
	persist(
		(set) => ({
			inboxViewMode: "list",
			setInboxViewMode: (value) => {
				set({ inboxViewMode: value })
			},
		}),
		{
			name: "inbox-view-mode",
		},
	),
)
